import { doc, setDoc, Timestamp } from "firebase/firestore";
import {
  deleteObject,
  getBytes,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../../firebaseConfig";
import { logReferenceAudit } from "./referenceAuditLog";

const sanitizePathSegment = (value) =>
  String(value || "")
    .trim()
    .replace(/[\\/#?]/g, "-");

const toTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  if (typeof value?.toDate === "function") return Timestamp.fromDate(value.toDate());

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
};

export const uploadReferenceAttachment = async ({
  file,
  userId,
  applicationId,
  referenceId,
  attachmentFolder,
}) => {
  if (!(file instanceof File)) return file;

  const fileName = sanitizePathSegment(file.name) || "attachment";
  const storagePath = [
    "Recruitment",
    sanitizePathSegment(userId),
    sanitizePathSegment(applicationId),
    sanitizePathSegment(referenceId),
    ...(attachmentFolder ? [sanitizePathSegment(attachmentFolder)] : []),
    fileName,
  ].join("/");
  const storageReference = ref(storage, storagePath);

  await uploadBytes(storageReference, file, {
    contentType: file.type || "application/octet-stream",
  });

  return {
    name: file.name,
    size: file.size,
    type: file.type || "application/octet-stream",
    path: storagePath,
    url: await getDownloadURL(storageReference),
    uploadedAt: Timestamp.now(),
  };
};

export const copyReferenceAttachmentToArchive = async (attachment) => {
  const sourceReference = ref(storage, attachment.path);
  const fileBytes = await getBytes(sourceReference);
  const pathParts = attachment.path.split("/");
  const originalFileName = pathParts.pop() || attachment.name || "attachment";
  const archivedFileName = `${Date.now()}-${sanitizePathSegment(originalFileName)}`;
  const archivePath = [...pathParts, "Archived", archivedFileName].join("/");
  const archiveReference = ref(storage, archivePath);

  await uploadBytes(archiveReference, fileBytes, {
    contentType: attachment.type || "application/octet-stream",
  });

  return {
    ...attachment,
    path: archivePath,
    url: await getDownloadURL(archiveReference),
    originalPath: attachment.path,
    archivedAt: Timestamp.now(),
  };
};

export const saveWorkHistoryReference = async ({
  applicationId,
  referenceId,
  history,
  form,
}) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error("You must be logged in to save a reference.");
  if (!applicationId || !referenceId) {
    throw new Error("The application or reference ID is missing.");
  }

  const isEmploymentReference = ["regulated", "non-regulated"].includes(
    form.referenceType,
  );
  const isRegulatedReference = form.referenceType === "regulated";
  const attachments = await Promise.all(
    (form.attachments || []).map((file) =>
      uploadReferenceAttachment({
        file,
        userId: user.uid,
        applicationId,
        referenceId,
      }),
    ),
  );

  const retainedPaths = new Set(
    attachments.map((attachment) => attachment?.path).filter(Boolean),
  );
  const removedAttachments = (history?.referenceAttachments || []).filter(
    (attachment) =>
      attachment?.path &&
      attachment.source !== "reference-consent-form" &&
      !retainedPaths.has(attachment.path),
  );
  const newlyArchivedAttachments = await Promise.all(
    removedAttachments.map(copyReferenceAttachmentToArchive),
  );

  const receivedReferenceAttachments = await Promise.all(
    (form.receivedReferenceAttachments || []).map((file) =>
      uploadReferenceAttachment({
        file,
        userId: user.uid,
        applicationId,
        referenceId,
        attachmentFolder: "received",
      }),
    ),
  );

  const savedHistory = {
    ...(history || {}),
    referenceStatus: form.status,
    referenceSent: toTimestamp(form.referenceSent),
    referenceFollowUp: form.status !== "reference-received" && form.followUpTimeframe && form.referenceSent
      ? toTimestamp(form.referenceFollowUp)
      : null,
    lastFollowedUpOn: toTimestamp(form.lastFollowedUpOn),
    referenceReceived: toTimestamp(form.referenceReceived),
    followUpTimeframe: form.followUpTimeframe,
    followUpSetDate: toTimestamp(form.followUpSetDate),
    skipReferenceFollowUp: Boolean(form.skipReferenceFollowUp),
    internalAdminNotes: form.internalAdminNotes || "",
    referenceType: form.referenceType || "",
    nameOfFirm: form.firmName || "",
    currentJobTitle: form.jobTitle || "",
    currentEmploymentStatus: form.employmentType || "",
    educationFrom: isEmploymentReference
      ? null
      : toTimestamp(form.educationFrom),
    educationTo: isEmploymentReference ? null : toTimestamp(form.educationTo),
    educationDetails: isEmploymentReference ? "" : form.educationDetails || "",
    educationalReferenceQuestions: isEmploymentReference
      ? []
      : form.questions || [],
    additionalReferenceQuestion: isEmploymentReference
      ? ""
      : form.additionalQuestion || "",
    from: isEmploymentReference
      ? toTimestamp(form.from)
      : toTimestamp(form.educationFrom),
    to: isEmploymentReference
      ? toTimestamp(form.to)
      : toTimestamp(form.educationTo),
    officeTelephone: form.firmTelephone || "",
    emailAddress: form.firmEmail || "",
    adviserPermission: form.adviserPermission || "no",
    currentlyEmployedAtFirm: isEmploymentReference
      ? form.currentlyEmployedAtFirm === "yes"
      : null,
    appointedRepresentative: isRegulatedReference && form.appointedRepresentative
      ? form.appointedRepresentative === "yes"
      : null,
    appointedRepresentativeDetails: isRegulatedReference
      ? form.appointedRepresentativeDetails || ""
      : "",
    lifeAssuranceCompany: isRegulatedReference
      ? form.appointedRepresentativeDetails || ""
      : "",
    reasonForNoReference: form.adviserReason ?? null,
    referenceRecipients: form.recipients || "",
    referenceSender: form.sender || "",
    referenceCc: form.cc || "",
    referenceBcc: form.bcc || "",
    referenceSubject: form.subject || "",
    referenceAttachments: attachments,
    receivedReferenceAttachments,
    archivedReferenceAttachments: [
      ...(history?.archivedReferenceAttachments || []),
      ...newlyArchivedAttachments,
    ],
    referenceMessageBody: form.messageBody || "",
    referenceUpdatedAt: Timestamp.now(),
    referenceUpdatedBy: user.email || user.uid,
  };

  const workHistoryDocument = doc(
    db,
    "Recruitment",
    applicationId,
    "Application Details",
    "Work History",
  );

  await setDoc(workHistoryDocument, { [referenceId]: savedHistory }, { merge: true });

  await logReferenceAudit({
    applicationId,
    referenceId,
    referenceKind: "work-history",
    action: "reference_saved",
    before: history || null,
    after: savedHistory,
    details: {
      form,
      confirmations: {
        detailsConfirmedBeforeSave: Boolean(form.saveConfirmed),
      },
    },
  });

  await Promise.allSettled(
    removedAttachments.map((attachment) =>
      deleteObject(ref(storage, attachment.path)),
    ),
  );

  return savedHistory;
};
